import { useNavigate } from 'react-router-dom';
import type { InsuranceProviderViewModel } from '@generated';
import { Plus, ChevronRight } from 'lucide-react';

interface OverviewTabProps {
	provider: InsuranceProviderViewModel;
}

export function OverviewTab({ provider }: OverviewTabProps) {
	const navigate = useNavigate();

	return (
		<div className="mt-6 space-y-6">
			{/* Provider Summary */}
			<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<h3 className="mb-4 text-lg font-semibold text-gray-900">Provider Information</h3>
				<div className="grid grid-cols-2 gap-4 text-sm">
					<div>
						<span className="text-gray-500">Insurance Type:</span>
						<span className="ml-2 font-medium">{provider.insuranceType}</span>
					</div>
					<div>
						<span className="text-gray-500">Payor Type:</span>
						<span className="ml-2 font-medium">{provider.payorType}</span>
					</div>
					<div>
						<span className="text-gray-500">Phone:</span>
						<span className="ml-2 font-medium">{provider.phoneNumber}</span>
					</div>
					{provider.faxNumber && (
						<div>
							<span className="text-gray-500">Fax:</span>
							<span className="ml-2 font-medium">{provider.faxNumber}</span>
						</div>
					)}
					{provider.emailAddress && (
						<div>
							<span className="text-gray-500">Email:</span>
							<span className="ml-2 font-medium">{provider.emailAddress}</span>
						</div>
					)}
					{provider.payerIdentifier && (
						<div>
							<span className="text-gray-500">Payer ID:</span>
							<span className="ml-2 font-medium">{provider.payerIdentifier}</span>
						</div>
					)}
					{provider.stateCode && (
						<div>
							<span className="text-gray-500">State Code:</span>
							<span className="ml-2 font-medium">{provider.stateCode}</span>
						</div>
					)}
					{provider.defaultBillingModel && (
						<div>
							<span className="text-gray-500">Default Billing Model:</span>
							<span className="ml-2 font-medium">{provider.defaultBillingModel}</span>
						</div>
					)}
				</div>
			</div>

			{/* Plans List */}
			<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold text-gray-900">Plans</h3>
					<button
						onClick={() => navigate(`plans/add`)}
						type="button"
						className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
					>
						<Plus className="size-4" />
						Add Plan
					</button>
				</div>
				{provider.plans.length === 0 ? (
					<p className="text-sm text-gray-500">No plans configured yet.</p>
				) : (
					<div className="space-y-2">
						{provider.plans.map(plan => (
							<div
								key={plan.id}
								onClick={() => navigate(`plans/${plan.id}`)}
								className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
							>
								<div>
									<p className="font-medium text-gray-900">{plan.name}</p>
									<div className="mt-1 flex gap-3 text-xs text-gray-500">
										<span className="rounded bg-blue-50 px-2 py-0.5 text-blue-700">{plan.billingModelLabel}</span>
										<span className="rounded bg-green-50 px-2 py-0.5 text-green-700">{plan.claimSubmissionModelLabel}</span>
									</div>
								</div>
								<ChevronRight className="size-5 text-gray-400" />
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
